import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { DataTag } from '@/components/DataTag/DataTag'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import SkeletonTable from '@/components/SkeletonTable'
import { routeToAccountManagementWithPrefilledEmail } from '@/features/AccountManagement/pages/AccountManagement'
import { getAuth0UserLink } from '@/features/HivesOverview/components/ExternalLinks'
import {
  useAddSupportUser,
  useGetOrgUsers,
  useRemoveSupportUser,
} from '@/generated/http-clients/v3ApiComponents'
import type { OrgUser } from '@/generated/http-clients/v3ApiSchemas'
import {
  ERROR_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  showOrUpdateToast,
} from '@/hooks/useToast/toastUtils'
import {
  Button,
  CloseButton,
  Dialog,
  Field,
  HStack,
  Icon,
  IconButton,
  Input,
  Table,
  Text,
} from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import get from 'lodash/get'
import { Plus, Trash2 } from 'lucide-react'
import type { FormEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type MembersCardProps = Readonly<{
  orgId: string
  canManageSupportUsers: boolean
}>

const ADD_SUPPORT_USER_TOAST_ID = 'add-support-user-toast'
const REMOVE_SUPPORT_USER_TOAST_ID = 'remove-support-user-toast'

export function MembersCard({
  orgId,
  canManageSupportUsers,
}: MembersCardProps) {
  const navigate = useNavigate()
  const { t: tCommon } = useTranslation(undefined, { useSuspense: false })
  const { t } = useTranslation('features', { useSuspense: false })

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<OrgUser | null>(null)
  const [reason, setReason] = useState('')

  const users = useGetOrgUsers({
    pathParams: { orgId },
  })

  const addSupportUser = useAddSupportUser({
    onSuccess: () => {
      showOrUpdateToast(ADD_SUPPORT_USER_TOAST_ID, {
        ...SUCCESS_TOAST_OPTION,
        title: String(t('orgDetails.members.toast.addSuccess')),
      })
      users.refetch()
      setIsAddDialogOpen(false)
      setReason('')
    },
    onError: (error) => {
      showOrUpdateToast(ADD_SUPPORT_USER_TOAST_ID, {
        ...ERROR_TOAST_OPTION,
        title: String(t('orgDetails.members.toast.addError')),
        description: get(error, 'message'),
      })
    },
  })

  const removeMember = useRemoveSupportUser({
    onSuccess: () => {
      showOrUpdateToast(REMOVE_SUPPORT_USER_TOAST_ID, {
        ...SUCCESS_TOAST_OPTION,
        title: String(t('orgDetails.members.toast.removeSuccess')),
      })
      users.refetch()
      setIsRemoveDialogOpen(false)
      setSelectedUser(null)
    },
    onError: (error) => {
      showOrUpdateToast(REMOVE_SUPPORT_USER_TOAST_ID, {
        ...ERROR_TOAST_OPTION,
        title: String(t('orgDetails.members.toast.removeError')),
        description: get(error, 'message'),
      })
    },
  })

  function handleAddMyself(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!reason.trim()) {
      return
    }

    addSupportUser.mutate({
      pathParams: { orgId },
      body: {
        userId: 'me',
        reason: reason.trim(),
      },
    })
  }

  function handleRemoveUser() {
    if (!selectedUser) {
      return
    }

    removeMember.mutate({
      pathParams: {
        orgId,
        userId: selectedUser.id,
      },
    })
  }

  function openRemoveDialog(user: OrgUser) {
    setSelectedUser(user)
    setIsRemoveDialogOpen(true)
  }

  return (
    <>
      <SectionCard
        title={t('orgDetails.members.title')}
        actions={
          canManageSupportUsers ? (
            <Button
              size="sm"
              colorPalette="brand"
              onClick={() => setIsAddDialogOpen(true)}
            >
              <Icon as={Plus} />
              {t('orgDetails.members.addButton')}
            </Button>
          ) : undefined
        }
      >
        {users.isError ? (
          <ApiErrorState
            apiResource={users as unknown as UseQueryResult}
            errorTitle={t('orgDetails.members.error.title')}
            errorDescription={t('orgDetails.members.error.description')}
          />
        ) : (
          <Table.Root variant="outline" colorPalette="gray">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>
                  {t('orgDetails.members.id')}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t('orgDetails.members.email')}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t('orgDetails.members.roles')}
                </Table.ColumnHeader>
                {canManageSupportUsers && <Table.ColumnHeader width="60px" />}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {users.isLoading && (
                <SkeletonTable
                  id="members-skeleton"
                  skeletonWidths={
                    canManageSupportUsers
                      ? [200, 180, 120, 40]
                      : [200, 180, 120]
                  }
                  rows={3}
                />
              )}
              {users.data?.map((user) => (
                <Table.Row key={`user-${user.id}`}>
                  <Table.Cell>
                    {user.id ? (
                      <DataTag
                        value={user.id}
                        fontWeight={500}
                        fontSize="1rem"
                        externalLink={getAuth0UserLink(user.id)}
                      >
                        {user.id}
                      </DataTag>
                    ) : (
                      user.id
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {user.email ? (
                      <DataTag
                        value={user.email}
                        fontWeight={500}
                        fontSize="1rem"
                        onClickBody={() =>
                          routeToAccountManagementWithPrefilledEmail(
                            navigate,
                            user.email,
                          )
                        }
                      >
                        {user.email}
                      </DataTag>
                    ) : (
                      tCommon('general.notAvailable')
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <HStack gap={1} flexWrap="wrap">
                      {user.roles?.length ? (
                        user.roles.map((role) => (
                          <DataTag
                            key={role}
                            value={role}
                            fontWeight={500}
                            fontSize="sm"
                            hasCopy={false}
                          >
                            {role}
                          </DataTag>
                        ))
                      ) : (
                        <Text fontSize="sm">
                          {tCommon('general.notAvailable')}
                        </Text>
                      )}
                    </HStack>
                  </Table.Cell>
                  {canManageSupportUsers &&
                    user.roles.some((role) => role === 'support_admin') && (
                      <Table.Cell>
                        <IconButton
                          aria-label={String(
                            t('orgDetails.members.removeButton'),
                          )}
                          size="xs"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => openRemoveDialog(user)}
                        >
                          <Icon as={Trash2} />
                        </IconButton>
                      </Table.Cell>
                    )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </SectionCard>

      {/* Add Support User Dialog */}
      <Dialog.Root
        open={isAddDialogOpen}
        onOpenChange={(details) => {
          if (!details.open) {
            setIsAddDialogOpen(false)
            setReason('')
          }
        }}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content as="form" onSubmit={handleAddMyself}>
            <Dialog.Header pb={3}>
              <Dialog.Title>
                {t('orgDetails.members.addDialog.title')}
              </Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton />
              </Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body>
              <Text fontSize="sm" mb={4}>
                {t('orgDetails.members.addDialog.description')}
              </Text>
              <Field.Root required>
                <Field.Label>
                  {t('orgDetails.members.addDialog.reasonLabel')}
                </Field.Label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={String(
                    t('orgDetails.members.addDialog.reasonPlaceholder'),
                  )}
                  size="lg"
                  borderColor="surface.500"
                  borderWidth="1px"
                />
                <Field.HelperText>
                  {t('orgDetails.members.addDialog.reasonHelper')}
                </Field.HelperText>
              </Field.Root>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAddDialogOpen(false)
                    setReason('')
                  }}
                >
                  {t('organization.features.actions.cancel')}
                </Button>
                <Button
                  type="submit"
                  colorPalette="brand"
                  loading={addSupportUser.isPending}
                  disabled={!reason.trim() || addSupportUser.isPending}
                >
                  {t('orgDetails.members.addDialog.submitButton')}
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      {/* Remove Support User Dialog */}
      <Dialog.Root
        open={isRemoveDialogOpen}
        onOpenChange={(details) => {
          if (!details.open) {
            setIsRemoveDialogOpen(false)
            setSelectedUser(null)
          }
        }}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header pb={3}>
              <Dialog.Title>
                {t('orgDetails.members.removeDialog.title')}
              </Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton />
              </Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body>
              <Text fontSize="sm">
                {t('orgDetails.members.removeDialog.message', {
                  email: selectedUser?.email,
                })}
              </Text>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsRemoveDialogOpen(false)
                    setSelectedUser(null)
                  }}
                >
                  {t('organization.features.actions.cancel')}
                </Button>
                <Button
                  colorPalette="red"
                  onClick={handleRemoveUser}
                  loading={removeMember.isPending}
                  disabled={removeMember.isPending}
                >
                  {t('orgDetails.members.removeDialog.confirmButton')}
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </>
  )
}
